import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';

import { Observable } from 'rxjs';

import { ModalComponent } from '@shared/components/UI/Modal/modal.component';

import {
  MODAL_ANIMATION_DURATION_MS,
  MODAL_CONFIG,
  MODAL_CONTENT_COMPONENT,
  MODAL_DATA,
  MODAL_OPEN_BODY_CLASS,
  MODAL_REF,
  ModalRef,
} from './modal.ref';
import {
  ModalCloseEvent,
  ModalOpenOptions,
  ResolvedModalConfig,
} from './modal.types';

const DEFAULT_CONFIG: ResolvedModalConfig = {
  size: 'md',
  title: '',
  description: '',
  closeOnBackdrop: true,
  closeOnEsc: true,
  showCloseButton: true,
  ariaLabelledby: 'modal-title',
  ariaDescribedby: 'modal-description',
};

@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private activeOverlayRef: OverlayRef | null = null;
  private activeModalRef: ModalRef<unknown> | null = null;
  private previouslyFocused: HTMLElement | null = null;

  public openModal<TResult = void, TData = unknown>(
    options: ModalOpenOptions<TData>,
  ): Observable<ModalCloseEvent<TResult>> {
    if (this.activeModalRef) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ModalService] A modal is already open. ' +
          'Close the current one before opening a new instance.',
      );
      return this.activeModalRef.afterClosed$ as Observable<ModalCloseEvent<TResult>>;
    }

    const { component, data, ...configFields } = options;

    const resolvedConfig: ResolvedModalConfig = {
      ...DEFAULT_CONFIG,
      ...configFields,
    };

    const modalRef = new ModalRef<TResult>();
    const overlayRef = this.buildOverlay();

    this.activeOverlayRef = overlayRef;
    this.activeModalRef = modalRef as unknown as ModalRef<unknown>;
    this.previouslyFocused = this.isBrowser
      ? (this.document.activeElement as HTMLElement | null)
      : null;

    const portalInjector = this.buildPortalInjector(modalRef, resolvedConfig, component, data);
    const portal = new ComponentPortal(ModalComponent, null, portalInjector);

    try {
      overlayRef.attach(portal);
      this.lockBodyScroll();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ModalService] Failed to attach modal portal:', error);
      this.cleanup(overlayRef);
      throw error;
    }

    modalRef.afterClosed$.subscribe({
      next: () => this.cleanup(overlayRef),
    });

    return modalRef.afterClosed$;
  }

  public closeActive(): void {
    this.activeModalRef?.close({ reason: 'programmatic' });
  }

  private buildOverlay(): OverlayRef {
    const config = new OverlayConfig({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position().global(),
      width: '100%',
      height: '100%',
      panelClass: 'modal-overlay-panel',
    });
    return this.overlay.create(config);
  }

  private buildPortalInjector<TResult, TData>(
    modalRef: ModalRef<TResult>,
    resolvedConfig: ResolvedModalConfig,
    contentComponent: ModalOpenOptions['component'],
    data: TData | undefined,
  ): Injector {
    return Injector.create({
      providers: [
        { provide: MODAL_REF, useValue: modalRef },
        { provide: MODAL_CONFIG, useValue: resolvedConfig },
        { provide: MODAL_DATA, useValue: data },
        { provide: MODAL_CONTENT_COMPONENT, useValue: contentComponent },
      ],
      parent: this.injector,
    });
  }

  private cleanup(overlayRef: OverlayRef): void {
    setTimeout(() => {
      overlayRef.dispose();
    }, MODAL_ANIMATION_DURATION_MS);

    this.unlockBodyScroll();
    this.restoreFocus();

    this.activeOverlayRef = null;
    this.activeModalRef = null;
  }

  // ─── Body scroll helpers ──────────────────────────────────────────────────

  /**
   * Prevents the `<body>` from scrolling by adding a CSS utility class.
   * CDK's `block` scroll strategy also handles this, but the explicit class
   * gives a reliable hook for custom CSS and ensures cleanup even if CDK
   * strategy fails to initialise.
   */
  private lockBodyScroll(): void {
    if (!this.isBrowser) {
      return;
    }

    this.document.body.classList.add(MODAL_OPEN_BODY_CLASS);
  }

  /**
   * Re-enables `<body>` scrolling by removing the CSS utility class added in
   * `lockBodyScroll`.
   */
  private unlockBodyScroll(): void {
    if (!this.isBrowser) {
      return;
    }

    this.document.body.classList.remove(MODAL_OPEN_BODY_CLASS);
  }

  // ─── Focus helpers ────────────────────────────────────────────────────────

  /**
   * Returns keyboard focus to the element that triggered the modal.
   * Guards against the case where that element was removed from the DOM.
   */
  private restoreFocus(): void {
    if (this.previouslyFocused?.isConnected) {
      this.previouslyFocused.focus();
    }
    this.previouslyFocused = null;
  }
}
